import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExternalLinkAlt,
  faTimes,
  faExpand,
  faAward,
  faSearchPlus,
  faSearchMinus,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import { trackEvent } from '../utils/analytics';

function Certificates({ title, provider, year, link, certificateImageUrl }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const openModal = () => {
    trackEvent('certificate_modal_open', {
      certificate_title: title
    });
    setIsModalOpen(true);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = (e) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.max(prev - 0.5, 1)); // Don't zoom out less than 1
    if (zoomLevel <= 1.5) setPosition({ x: 0, y: 0 }); // Reset position if zooming back to normal
  };

  const handleResetZoom = (e) => {
    e.stopPropagation();
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setStartPos({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      e.preventDefault();
      setPosition({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };


  return (
    <>
      {/* Visual List Item (Media Object Style) */}
      <div
        className="group flex flex-col md:flex-row gap-6 p-5 bg-bg-card rounded-xl border border-border-color hover:border-primary/30 hover:shadow-lg transition-all duration-300 certificate-card"
      >
        {/* Left: Image Thumbnail (Prominent but Controlled) */}
        <div
          className="w-full md:w-48 h-32 shrink-0 relative rounded-lg overflow-hidden border border-border-color cursor-pointer bg-gray-100 dark:bg-gray-800"
          onClick={openModal}
        >
          {certificateImageUrl ? (
            <>
              <img
                src={certificateImageUrl}
                alt={title}
                className="w-full h-full object-contain p-1 transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <FontAwesomeIcon icon={faExpand} className="text-white opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all drop-shadow-md" />
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-text-muted">
              <FontAwesomeIcon icon={faAward} size="2x" />
            </div>
          )}
        </div>

        {/* Right: Content */}
        <div className="flex-1 flex flex-col justify-center min-w-0">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-xl font-serif font-bold text-text-primary mb-2 leading-tight group-hover:text-primary transition-colors">
                {title}
              </h3>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm text-text-secondary">
                <span className="font-semibold text-text-primary">{provider}</span>
                {year && (
                  <>
                    <span className="hidden sm:inline text-text-muted">•</span>
                    <span className="font-mono text-xs bg-bg-surface px-2 py-0.5 rounded text-text-muted">
                      {year}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* External Link Action */}
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 p-2 text-text-muted hover:text-primary transition-colors rounded-full hover:bg-primary/10"
                title="Verify Credential"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>
            )}
          </div>

          {/* Optional: View Certificate Text Button for clarity */}
          <div className="mt-4 md:mt-auto pt-2">
             <button
               onClick={openModal}
               className="text-xs font-medium text-primary hover:underline decoration-primary/30 underline-offset-4"
             >
               View Certificate Image
             </button>
          </div>
        </div>
      </div>

      {/* Modal Overlay */}
      {isModalOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-fadeIn"
          onClick={closeModal}
        >
          <div
            className="relative max-w-5xl w-full max-h-[95vh] bg-bg-card rounded-xl overflow-hidden shadow-2xl flex flex-col animate-scaleIn"
            onClick={e => e.stopPropagation()}
          >
             <div className="flex justify-between items-center p-4 border-b border-border-color bg-bg-surface z-10 relative">
                <div>
                  <h4 className="font-bold text-lg text-text-primary font-serif select-none">{title}</h4>
                  <p className="text-sm text-text-secondary select-none">{provider}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center bg-bg-main rounded-lg border border-border-color p-1 mr-4">
                    <button
                      onClick={handleZoomOut}
                      className="p-2 hover:bg-bg-surface rounded-md text-text-secondary disabled:opacity-50 transition-colors"
                      disabled={zoomLevel <= 1}
                      title="Zoom Out"
                    >
                      <FontAwesomeIcon icon={faSearchMinus} />
                    </button>
                    <span className="px-2 text-sm font-mono text-text-muted min-w-[3rem] text-center select-none">
                      {Math.round(zoomLevel * 100)}%
                    </span>
                    <button
                      onClick={handleZoomIn}
                      className="p-2 hover:bg-bg-surface rounded-md text-text-primary disabled:opacity-50 transition-colors"
                      disabled={zoomLevel >= 3}
                      title="Zoom In"
                    >
                      <FontAwesomeIcon icon={faSearchPlus} />
                    </button>
                    <button
                      onClick={handleResetZoom}
                      className="p-2 hover:bg-bg-surface rounded-md text-text-secondary ml-1 border-l border-border-color pl-3 transition-colors"
                      title="Reset Zoom"
                    >
                      <FontAwesomeIcon icon={faUndo} size="sm" />
                    </button>
                  </div>
                  <button
                    onClick={closeModal}
                    className="text-text-muted hover:text-text-primary transition-colors p-2 rounded-full hover:bg-bg-surface"
                    title="Close"
                  >
                    <FontAwesomeIcon icon={faTimes} size="lg" />
                  </button>
                </div>
             </div>

             <div
               className="flex-1 overflow-auto p-4 bg-bg-main flex items-center justify-center min-h-[300px] relative cursor-grab active:cursor-grabbing"
               onMouseDown={handleMouseDown}
               onMouseMove={handleMouseMove}
               onMouseUp={handleMouseUp}
               onMouseLeave={handleMouseUp}
             >
               <img
                 src={certificateImageUrl}
                 alt={title}
                 className="max-w-full max-h-[80vh] object-contain shadow-lg rounded transition-transform duration-200 ease-out origin-center"
                 style={{
                   transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                   cursor: zoomLevel > 1 ? 'grab' : 'default'
                 }}
                 draggable={false}
               />
             </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default Certificates;
